'use client';
import { useState, useEffect } from 'react';
import { useTheme } from 'next-themes';
import Image from 'next/image';

import "./_like.scss"

interface LikeProps {
  itemId: number;
  itemType: string;
}

const Like = ({ itemId, itemType }: LikeProps) => {
  const { theme } = useTheme();
  const [isLiked, setIsLiked] = useState(false);
  const storageKey = `${itemType}-likes`;

  useEffect(() => {
    const likes = JSON.parse(localStorage.getItem(storageKey) || '[]');
    setIsLiked(likes.includes(itemId));
  }, [itemId, storageKey]);

  const handleLike = async () => {
    const likes = JSON.parse(localStorage.getItem(storageKey) || '[]');
    const isNowLiked = !isLiked;
    const updatedLikes = isNowLiked
      ? [...likes, itemId]
      : likes.filter((id: number) => id !== itemId);

    setIsLiked(isNowLiked);
    localStorage.setItem(storageKey, JSON.stringify(updatedLikes));

    try {
      const method = isNowLiked ? 'POST' : 'DELETE';
      const response = await fetch(`/api/community/like/${itemType}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemId }),
      });

      if (!response.ok) {
        console.error('Failed to update like status on the server');
        setIsLiked(!isNowLiked);
        localStorage.setItem(storageKey, JSON.stringify(likes));
      }
    } catch (error) {
      console.error('Network error:', error);
      setIsLiked(!isNowLiked);
      localStorage.setItem(storageKey, JSON.stringify(likes));
    }
  };

  return (
    <div
      className={`like-button ${isLiked ? 'liked' : 'not-liked'}`}
      onClick={handleLike}
      aria-label={isLiked ? 'Unlike' : 'Like'}
    >
      {isLiked ? (
        <Image
          src="/icons/like-red-filled50x50.svg"
          alt="liked"
          width={30}
          height={30}
          loading="lazy" 
          className="like-icon"
        />
      ) : (
        <Image
          src={
            theme === 'dark'
              ? '/icons/like-dark-unfilled50x50.svg'
              : '/icons/like-light-unfilled50x50.svg'
          }
          alt="unliked"
          width={30}
          height={30}
          loading="lazy" 
          className="like-icon"
        />
      )}
      {isLiked && (
        <>
        <div className="sparkle sparkle-1"></div>
        <div className="sparkle sparkle-2"></div>
        <div className="sparkle sparkle-3"></div>
        <div className="sparkle sparkle-4"></div>
        <div className="sparkle sparkle-5"></div>
        <div className="sparkle sparkle-6"></div>
        </>
      )}
    </div>
  );
};

export default Like;
